/*
    Copyright (c) Microsoft Corporation. All rights reserved.
    Licensed under the MIT license.
*/
define(["require", "exports", "azure-devops-ui/Core/Observable", "azure-devops-ui/Core/Util/String", "azure-devops-ui/Status", "../Contracts/Contracts", "../Resources"], function (require, exports, Observable_1, String_1, Status_1, Contracts_1, Resources) {
    "use strict";
    Object.defineProperty(exports, "__esModule", { value: true });
    var pipelineNameAnnotationKey = "azure-pipelines/pipeline";
    var pipelineRunIdAnnotationKey = "azure-pipelines/execution";
    var pipelineRunUrlAnnotationKey = "azure-pipelines/executionuri";
    var newPipelineRunIdAnnotationKey = "azure-pipelines/run";
    var newPipelineRunUrlAnnotationKey = "azure-pipelines/runuri";
    var pipelineJobNameAnnotationKey = "azure-pipelines/jobName";
    var matchPatternForImageName = new RegExp(/\:\/\/(.+?)\@/);
    var matchPatternForDigest = new RegExp(/\@sha256\:(.+)/);
    var Utils = /** @class */ (function () {
        function Utils() {
        }
        Utils.isOwnerMatched = function (objectMeta, ownerUIdLowerCase) {
            return objectMeta.ownerReferences
                && objectMeta.ownerReferences.length > 0
                && objectMeta.ownerReferences.some(function (o) { return o.uid.toLowerCase() === ownerUIdLowerCase; });
        };
        Utils.getUILabelModelArray = function (items) {
            var labelArray = new Observable_1.ObservableArray();
            if (items) {
                Object.keys(items).forEach(function (key) {
                    labelArray.push({ content: String_1.format("{0}={1}", key, items[key]) });
                });
            }
            return labelArray;
        };
        Utils.getPillTags = function (items) {
            var tags = [];
            if (items) {
                Object.keys(items).forEach(function (key) {
                    // trim single/double quotes
                    var keyValue = (items[key] || "").trim().replace(/^("|')+/, "").replace(/("|')+$/, "");
                    tags.push(String_1.format("{0}={1}", key, keyValue));
                });
            }
            return tags;
        };
        Utils.getPipelineText = function (annotations) {
            var pipelineName = "";
            var pipelineExecutionId = "";
            var keys = annotations ? Object.keys(annotations) : [];
            keys.find(function (key) {
                var keyVal = key.toLowerCase();
                if (!pipelineName && keyVal === pipelineNameAnnotationKey) {
                    pipelineName = annotations[key];
                }
                else if (!pipelineExecutionId && (keyVal === pipelineRunIdAnnotationKey || keyVal === newPipelineRunIdAnnotationKey)) {
                    pipelineExecutionId = annotations[key];
                }
                return !!pipelineName && !!pipelineExecutionId;
            });
            return pipelineName && pipelineExecutionId ? String_1.localeFormat("{0} / {1}", pipelineName, pipelineExecutionId) : "";
        };
        Utils.getPipelineDetails = function (annotations) {
            if (!annotations) {
                return {};
            }
            var runName = annotations[newPipelineRunIdAnnotationKey] || annotations[pipelineRunIdAnnotationKey];
            var runUrl = annotations[newPipelineRunUrlAnnotationKey] || annotations[pipelineRunUrlAnnotationKey];
            return {
                jobName: annotations[pipelineJobNameAnnotationKey],
                pipelineName: annotations[pipelineNameAnnotationKey],
                runName: runName,
                runUrl: runUrl
            };
        };
        Utils.getPodsStatusProps = function (currentPods, desiredPods) {
            var statusProps = undefined;
            var podsText = "", podsTooltip = "";
            if (desiredPods != null && desiredPods > 0) {
                var availableCount = currentPods == null || currentPods <= 0 ? 0 : currentPods;
                var diffCount = desiredPods - availableCount;
                statusProps = availableCount < desiredPods ? Status_1.Statuses.Failed : Status_1.Statuses.Success;
                podsText = String_1.localeFormat("{0}/{1}", availableCount, desiredPods);
                podsTooltip = availableCount === desiredPods
                    ? Resources.AllPodsRunningText
                    : diffCount === 1 ? Resources.PodNotReadyText : String_1.localeFormat(Resources.PodsNotReadyText, diffCount);
            }
            return { statusProps: statusProps, pods: podsText, podsTooltip: podsTooltip };
        };
        Utils.generateEqualsConditionLabelSelector = function (labels) {
            var labelSelector = "";
            if (labels) {
                var keySet_1 = Object.keys(labels);
                keySet_1.forEach(function (key, index) {
                    labelSelector = labelSelector.concat(String_1.format("{0}={1}", key, labels[key]));
                    if (index < keySet_1.length - 1)
                        labelSelector = labelSelector.concat(",");
                });
            }
            return labelSelector;
        };
        Utils.generatePodStatusProps = function (status) {
            var statusProps = Status_1.Statuses.Failed;
            var tooltip = status.message || status.phase;
            if (status.phase === Contracts_1.PodPhase.Running || status.phase === Contracts_1.PodPhase.Succeeded) {
                statusProps = Status_1.Statuses.Success;
            }
            // check containers status and then conditions
            // container state could be Waiting/Running/Terminated
            // https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle/#pod-and-container-status
            var failedContainerState = (status.containerStatuses || []).find(function (cs) { return !cs.ready && !cs.state.running; });
            if (failedContainerState) {
                var failedState = failedContainerState.state;
                statusProps = Status_1.Statuses.Failed;
                tooltip = failedState.waiting && (failedState.waiting.message || failedState.waiting.reason)
                    || failedState.terminated && (failedState.terminated.message || failedState.terminated.reason);
            }
            else {
                // https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle/#pod-conditions
                // condition status could be True/False/Unknown
                var hasConditionFailed_1 = function (c) { return (typeof c.status === "string") && c.status.toLowerCase() !== "true" && (!!c.message || !!c.reason); };
                var failedCondition = (status.conditions || []).find(function (c) { return hasConditionFailed_1(c); });
                if (failedCondition) {
                    statusProps = Status_1.Statuses.Failed;
                    tooltip = failedCondition.message || failedCondition.reason;
                }
            }
            return { statusProps: statusProps, tooltip: tooltip };
        };
        Utils.filterByName = function (objectName, filterKey) {
            if (filterKey) {
                return objectName.includes(filterKey);
            }
            return true;
        };
        Utils.getImageText = function (podSpec) {
            var images = [];
            var imageText = "";
            var imageTooltipText = "";
            if (podSpec && podSpec.containers && podSpec.containers.length > 0) {
                podSpec.containers.forEach(function (container) {
                    if (images.indexOf(container.image) < 0) {
                        images.push(Utils.appendDefaultTagToImageName(container.image));
                    }
                });
                if (images.length > 1) {
                    imageText = String_1.localeFormat(Resources.MoreImagesText, images[0], images.length - 1);
                    imageTooltipText = images.join(", ");
                }
                else {
                    imageText = images[0];
                }
            }
            return { imageText: imageText, imageTooltipText: imageTooltipText };
        };
        Utils.getFirstImageName = function (podSpec) {
            if (podSpec && podSpec.containers && podSpec.containers.length > 0) {
                return Utils.appendDefaultTagToImageName(podSpec.containers[0].image);
            }
            return "";
        };
        Utils.getFirstContainerName = function (podSpec) {
            if (podSpec && podSpec.containers && podSpec.containers.length > 0) {
                return podSpec.containers[0].name;
            }
            return "";
        };
        Utils.getImageIdsForPods = function (pods) {
            var imageIds = [];
            for (var _i = 0, pods_1 = pods; _i < pods_1.length; _i++) {
                var pod = pods_1[_i];
                var podStatus = pod.status;
                if (podStatus.containerStatuses && podStatus.containerStatuses.length > 0) {
                    for (var _a = 0, _b = podStatus.containerStatuses; _a < _b.length; _a++) {
                        var containerStatus = _b[_a];
                        // Construct the query URL as per Grafeas format
                        var imgId = Utils.getImageResourceUrl(containerStatus.imageID);
                        if (imgId && (imageIds.length == 0 || imageIds.indexOf(imgId) < 0))
                            imageIds.push(imgId);
                    }
                }
            }
            return imageIds;
        };
        Utils.getImageIdForWorkload = function (containerName, pods, ownerId) {
            // The image name in parent.spec.template.spec.containers and in pod.status.containerStatuses is not a constant, example it is redis in former, and redis:latest in latter
            // Hence filtering the pods on the basis of container name which is a constant
            var getImageIdForMatchingPods = function (pods) {
                for (var _i = 0, pods_2 = pods; _i < pods_2.length; _i++) {
                    var pod = pods_2[_i];
                    var podStatus = pod.status;
                    if (podStatus.containerStatuses && podStatus.containerStatuses.length > 0) {
                        // Return the imageId for the first matching pod
                        var containerStatusForGivenImage = podStatus.containerStatuses.find(function (status) { return status.name.toLowerCase() === containerName.toLowerCase(); });
                        if (containerStatusForGivenImage && containerStatusForGivenImage.imageID) {
                            // Construct the query URL as per Grafeas format
                            return Utils.getImageResourceUrl(containerStatusForGivenImage.imageID);
                        }
                    }
                }
                return "";
            };
            if (ownerId && pods.length > 0) {
                var matchingPods = pods.filter(function (pod) { return pod.metadata && Utils.isOwnerMatched(pod.metadata, ownerId.toLowerCase()); });
                return getImageIdForMatchingPods(matchingPods);
            }
            else {
                return getImageIdForMatchingPods(pods);
            }
        };
        Utils.getImageResourceUrlParameter = function (imageId, matchPattern) {
            var imageMatch = imageId.match(matchPattern);
            if (imageMatch && imageMatch.length >= 1) {
                return imageMatch[1];
            }
            return "";
        };
        Utils.getImageResourceUrl = function (imageId) {
            var sha256Text = "@sha256:";
            var separator = "://";
            var indexOfSeparator = imageId.indexOf(separator);
            var image = indexOfSeparator >= 0 ? imageId.substr(indexOfSeparator + separator.length) : imageId;
            var digest = Utils.getImageResourceUrlParameter(imageId, matchPatternForDigest);
            // This match pattern is copied over from DockerV2 task to maintain parity between image Urls
            var match = image.match(/^(?:([^\/]+)\/)?(?:([^\/]+)\/)?([^@:\/]+)(?:[@:](.+))?$/);
            if (!match) {
                return "";
            }
            var registry = match[1];
            var imgNamespace = match[2];
            var repository = match[3];
            if (!imgNamespace && registry && !/[:.]/.test(registry)) {
                imgNamespace = registry;
                registry = "docker.io";
            }
            if (!imgNamespace && !registry) {
                registry = "docker.io";
                imgNamespace = "library";
            }
            registry = registry ? registry + "/" : "";
            imgNamespace = imgNamespace ? imgNamespace + "/" : "";
            return String_1.format("https://{0}{1}{2}{3}{4}", registry, imgNamespace, repository, sha256Text, digest);
        };
        Utils.extractDisplayImageName = function (imageId) {
            return Utils.getImageResourceUrlParameter(imageId, matchPatternForImageName);
        };
        Utils.appendDefaultTagToImageName = function (imageName) {
            if (!/:/.test(imageName)) {
                imageName = String_1.format("{0}:latest", imageName);
            }
            return imageName;
        };
        /**
         * We will deprecate this function as soon as we decide on a better mapping. Temporarily using this
         * @param kind
         */
        Utils.getItemTypeKeyFromKind = function (kind) {
            switch (kind) {
                case "ReplicaSet": return "replica-set" /* ReplicaSetKey */;
                case "DaemonSet": return "daemon-set" /* DaemonSetKey */;
                case "StatefulSet": return "stateful-set" /* StatefulSetKey */;
                case "Service": return "service-item" /* ServiceItemKey */;
                case "Pod": return "orphan-pod" /* OrphanPodKey */;
                default: return "";
            }
        };
        /**
         * We will deprecate this function as soon as we decide on a better mapping. Temporarily using this
         * @param typeKey
         */
        Utils.getKindFromItemTypeKey = function (typeKey) {
            switch (typeKey) {
                case "daemon-set" /* DaemonSetKey */: return "DaemonSet";
                case "replica-set" /* ReplicaSetKey */: return "ReplicaSet";
                case "stateful-set" /* StatefulSetKey */: return "StatefulSet";
                case "orphan-pod" /* OrphanPodKey */: return "Pod";
                case "service-item" /* ServiceItemKey */: return "Service";
                default: return "";
            }
        };
        Utils.copyToClipboard = function (text) {
            // IE11 Compatible
            if (window.clipboardData) {
                window.clipboardData.setData("text", text);
                return;
            }
            var listener = function (e) {
                var clipboardData = e.clipboardData;
                if (clipboardData) {
                    clipboardData.setData("text/plain", text);
                }
                e.preventDefault();
            };
            // Add a listener to the copy event, and add it to the clipboard. Then remove the handler immediately, as it prevents default
            document.addEventListener("copy", listener);
            document.execCommand("copy");
            document.removeEventListener("copy", listener);
        };
        return Utils;
    }());
    exports.Utils = Utils;
});
